import { useDispatch } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router";
import { useLocation } from "react-router";

import { searchEntity } from "../services/API/searchFunc";
import { getAllPatients } from "../services/API/Patients";
import { getAllUsers } from "../services/API/Users";
import { RootState } from "../store/store";
import { setData, setDataLength } from "@/store/data/dataSlice";

export default function useSearch(url: string, query: string) {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialPage = Number(searchParams.get("page")) || 1;
    const limitPage = Number(searchParams.get("limit")) || 15;
    const sort = searchParams.get("sort") || "-createdAt";

    const [activepage, setActivePage] = useState<number>(initialPage);
    const [sortOption, setSortOption] = useState<string>(sort);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const dispatch = useDispatch();

    const myLocation = useLocation();
    const delay = 500;
    const patientDoctorSelectorInDrawer = useSelector(
        (state: RootState) => state.ui.patientDoctorSelectorInDrawer,
    );

    function handleChangeActivepage(
        event: React.ChangeEvent<unknown>,
        value: number,
    ) {
        setActivePage(value);
    }

    function handleChangeSortOpton(selectedId: string) {
        setSortOption(selectedId);
    }

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        let response;

        try {
            if (query.length === 0) {
                if (
                    myLocation.pathname.includes("users") ||
                    patientDoctorSelectorInDrawer === "doctor"
                ) {
                    response = await getAllUsers(
                        limitPage,
                        activepage,
                        sortOption,
                    );
                } else if (
                    myLocation.pathname.includes("patients") ||
                    patientDoctorSelectorInDrawer === "patient"
                ) {
                    response = await getAllPatients(
                        limitPage,
                        activepage,
                        sortOption,
                    );
                }
            } else {
                response = await searchEntity(`${url}?query=${query}`);
            }

            if (response?.data) {
                dispatch(setData(response.data));
                dispatch(setDataLength(response.dataLength));
            } else {
                toast.error(response?.message || "Не вдалося отримати дані");
            }
        } catch (error) {
            toast.error("Сталася помилка при завантаженні даних.");
        } finally {
            setIsLoading(false);
        }
    }, [
        query,
        url,
        activepage,
        limitPage,
        sortOption,
        dispatch,
        myLocation.pathname,
    ]);

    useEffect(() => {
        setActivePage(1);
    }, [sortOption]);

    useEffect(() => {
        let flag = true;
        if (query.length > 0 && flag) {
            setActivePage(1);
            flag = false;
        }
    }, [query]);

    useEffect(() => {
        if (query.length === 0) {
            fetchData();
        } else {
            const timeoutId = setTimeout(() => {
                fetchData();
            }, delay);

            return () => clearTimeout(timeoutId);
        }
    }, [query, url, delay, fetchData, activepage, limitPage, setSearchParams]);

    useEffect(() => {
        if (
            myLocation.pathname === "/patients" ||
            myLocation.pathname === "/users"
        ) {
            setSearchParams({
                page: activepage.toString(),
                limit: limitPage.toString(),
                sort: sortOption,
            });
        }
    }, [
        myLocation.pathname,
        activepage,
        limitPage,
        sortOption,
        setSearchParams,
    ]);
    return {
        isLoading,
        activepage,
        limitPage,
        sortOption,
        handleChangeActivepage,
        handleChangeSortOpton,
    };
}
